import { useCallback, useEffect, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { TFunction } from "i18next";
import toast from "react-hot-toast";

import type {
  TenantCurrentResponse,
  TenantPlanOption,
} from "@/services/authService";
import { updateTenantPlan } from "@/services/authService";
import { createPlanCheckout } from "@/services/planPaymentsService";
import { recordFiscalBenefitDeclaration } from "@/services/fiscalService";
import { planUsesActivationPath } from "@/pages/onboarding/planActivation";
import type { FiscalBenefitDeclarationValue } from "@/components/features/fiscal-benefit/FiscalBenefitDeclaration";

interface UsePlanGateParams {
  tenant: TenantCurrentResponse | null;
  loading: boolean;
  viewStep: number;
  totalSteps: number;
  isAccountantFlowOwner: boolean;
  isAccountantUser: boolean;
  selectedPlanId: string | null;
  selectedBillingInterval: "monthly" | "annual";
  plans: TenantPlanOption[];
  benefitDeclaration: FiscalBenefitDeclarationValue;
  setSavingPlan: (value: boolean) => void;
  setViewStep: Dispatch<SetStateAction<number>>;
  loadTenant: () => Promise<void>;
  finalizeAccountantHandoff: () => Promise<void>;
  t: TFunction;
}

/**
 * Owns the pre-DGI plan gate: whether the gate is active (`needsPlanGate`), the
 * continue handler that saves the free plan or kicks off the dLocal Go redirect,
 * the plan_payment done/failed/timeout URL-return effects, and the shared
 * `persistBenefitDeclarationIfNeeded` helper (consumed by the step machine's own
 * checkout branches in handleFinish / handleNextStep).
 */
export function usePlanGate({
  tenant,
  loading,
  viewStep,
  totalSteps,
  isAccountantFlowOwner,
  isAccountantUser,
  selectedPlanId,
  selectedBillingInterval,
  plans,
  benefitDeclaration,
  setSavingPlan,
  setViewStep,
  loadTenant,
  finalizeAccountantHandoff,
  t,
}: UsePlanGateParams) {
  // Régimen (viewStep 2) and plan (viewStep 3) are now real, back-navigable
  // steps that precede situation for the two plan-first families: "Vos lo hacés"
  // (onboardingMode=self) and "Lo hace mi contador" (accountant-flow owner, whose
  // plan step is the ONLY owner-facing step before the handoff). The accountant
  // (isAccountantUser) is never gated, and a tenant that already completed
  // onboarding is left alone (no retroactive gate).
  //
  // The gates are POSITION-based (which viewStep you are on), NOT completion-based
  // (whether planId/declaration exist). That is what makes Back navigable: pressing
  // Back from situation(4) -> plan(3) -> régimen(2) re-renders each step even though
  // its tenant field is already set. resolveStep routes régimen-vs-plan from the
  // durable `fiscalBenefitDeclared` / `planId` fields, so refresh reconstructs the
  // right position; these booleans just say "the current viewStep IS that step".
  const ownerCanUsePrePlan =
    !isAccountantUser &&
    tenant != null &&
    (tenant.onboardingMode === "self" || isAccountantFlowOwner) &&
    !tenant.onboardingCompletedAt;
  const needsRegimeGate = ownerCanUsePrePlan && viewStep === 2;
  const needsPlanGate = ownerCanUsePrePlan && viewStep === 3;

  /**
   * Persist the Decreto 206/019 declaration before the plan is activated.
   *
   * Must run BEFORE any activation call: the backend refuses to activate a plan
   * for a tenant with no current declaration, so posting it afterwards would 403
   * the very step it is meant to unblock.
   *
   * Defensive about a null answer even though Next is gated on it — the button
   * guard is the UI contract, this is the data contract, and an untouched form
   * must never be written as a negative declaration.
   */
  const persistBenefitDeclarationIfNeeded = async (): Promise<void> => {
    if (benefitDeclaration.complies == null) return;
    await recordFiscalBenefitDeclaration({
      complies: benefitDeclaration.complies,
      motivo: benefitDeclaration.motivo,
    });
  };

  const planPaymentDoneHandledRef = useRef(false);
  useEffect(() => {
    if (loading || !tenant) return;
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("plan_payment");
    if (!flag) {
      planPaymentDoneHandledRef.current = false;
      return;
    }
    if (flag !== "done" || planPaymentDoneHandledRef.current) return;
    planPaymentDoneHandledRef.current = true;
    params.delete("plan_payment");
    params.delete("checkout_id");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}`
    );
    void loadTenant().then(() => {
      // Accountant handoff: the owner just paid for the plan. Send the
      // accountant invite (email stashed pre-redirect) and route to the
      // dashboard instead of resuming the self-onboarding wizard.
      if (isAccountantFlowOwner) {
        void finalizeAccountantHandoff();
        return;
      }
      toast.success(t("onboarding.saved"));
      setViewStep(totalSteps);
    });
  }, [
    loading,
    tenant,
    totalSteps,
    loadTenant,
    t,
    isAccountantFlowOwner,
    finalizeAccountantHandoff,
    setViewStep,
  ]);

  const planPaymentFailHandledRef = useRef(false);
  useEffect(() => {
    if (loading || !tenant) return;
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("plan_payment");
    if (!flag) {
      planPaymentFailHandledRef.current = false;
      return;
    }
    if (flag !== "failed" && flag !== "timeout") return;
    if (planPaymentFailHandledRef.current) return;
    planPaymentFailHandledRef.current = true;
    params.delete("plan_payment");
    params.delete("checkout_id");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}`
    );
    toast.error(
      t(flag === "timeout" ? "pay.planPaymentTimeout" : "pay.planPaymentFailed")
    );
  }, [loading, tenant, t]);

  /**
   * Pre-DGI plan gate: save the chosen plan (free) or kick off the dLocal
   * Go redirect (paid). On success the gate clears (tenant.planId set) and
   * the user resumes at viewStep=3 which now renders DgiAccessStep.
   */
  const handlePlanGateContinue = useCallback(async () => {
    if (!selectedPlanId) return;
    // Re-checkout guard: the plan step is now reachable via Back with a plan
    // already on file. If the chosen tier is unchanged, do NOT open a fresh
    // dLocal checkout (which would re-charge) — just advance to the next step.
    // resolveStep agrees (planId set -> situation), so prev+1 lands on situation
    // for self; the accountant-flow owner can't sit here with planId set (it
    // routes straight to handoff), so this branch is self-only in practice.
    if (tenant?.planId && tenant.planId === selectedPlanId) {
      setViewStep((prev) => prev + 1);
      return;
    }
    setSavingPlan(true);
    try {
      if (planUsesActivationPath(selectedPlanId, selectedBillingInterval, plans)) {
        // Free OR partner-discounted paid: activate via updateTenantPlan, which
        // returns a dLocal redirectUrl for any partner-covered remainder.
        const res = await updateTenantPlan(
          selectedPlanId,
          selectedBillingInterval,
          "onboarding"
        );
        await loadTenant();
        if (res.redirectUrl) {
          window.location.href = res.redirectUrl;
          return;
        }
        if (isAccountantFlowOwner) {
          await finalizeAccountantHandoff();
          return;
        }
        toast.success(t("onboarding.saved"));
        return;
      }
      const res = await createPlanCheckout({
        planId: selectedPlanId,
        billingInterval: selectedBillingInterval,
        returnContext: "onboarding",
      });
      await loadTenant();
      if (res.redirectUrl) {
        window.location.href = res.redirectUrl;
        return;
      }
      if (isAccountantFlowOwner) {
        await finalizeAccountantHandoff();
        return;
      }
      toast.success(t("onboarding.saved"));
    } catch {
      toast.error(t("onboarding.errorSave"));
    } finally {
      setSavingPlan(false);
    }
  }, [
    selectedPlanId,
    selectedBillingInterval,
    plans,
    setSavingPlan,
    setViewStep,
    tenant?.planId,
    loadTenant,
    t,
    isAccountantFlowOwner,
    finalizeAccountantHandoff,
  ]);

  /**
   * Régimen gate continue: persist the Decreto 206/019 declaration, then reload.
   * Once persisted, `fiscalBenefitDeclared` flips true; resolveStep then routes
   * the tenant forward from régimen(2) to the plan gate(3) on the reload — the
   * same "routed by tenant fields" advance the rest of the machine uses.
   */
  const handleRegimeGateContinue = useCallback(async () => {
    // Read benefitDeclaration fresh here (it's in deps) — NOT via the closured
    // persistBenefitDeclarationIfNeeded, which would capture a stale value and
    // silently no-op, leaving the gate stuck.
    if (benefitDeclaration.complies == null) return;
    setSavingPlan(true);
    try {
      await recordFiscalBenefitDeclaration({
        complies: benefitDeclaration.complies,
        motivo: benefitDeclaration.motivo,
      });
      await loadTenant();
    } catch {
      toast.error(t("onboarding.errorSave"));
    } finally {
      setSavingPlan(false);
    }
  }, [benefitDeclaration, setSavingPlan, loadTenant, t]);

  return {
    needsPlanGate,
    needsRegimeGate,
    handlePlanGateContinue,
    handleRegimeGateContinue,
    persistBenefitDeclarationIfNeeded,
  };
}
